export const patientGenders = ['male', 'female', 'other', 'prefer-not-to-say'] as const;
export const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'unknown'] as const;

export interface Patient {
  id: string;
  medicalRecordNumber: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: (typeof patientGenders)[number];
  phone: string;
  email?: string;
  bloodGroup: (typeof bloodGroups)[number];
  address?: string;
  state?: string;
  district?: string;
  pinCode?: string;
  allergies: string[];
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  status: 'active' | 'inactive' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface PatientPayload {
  medicalRecordNumber: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: Patient['gender'];
  phone: string;
  email?: string;
  bloodGroup: Patient['bloodGroup'];
  address?: string;
  state?: string;
  district?: string;
  pinCode?: string;
  allergies: string[];
  status: Patient['status'];
}

export interface PatientLookupOption {
  id: string;
  label: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PatientListResponse {
  success: true;
  data: Patient[];
  pagination: Pagination;
}

export interface PatientResponse {
  success: true;
  data: Patient;
}

export interface PatientStats {
  total: number;
  active: number;
  archived: number;
  registeredToday: number;
  registeredYesterday: number;
  trend: { date: string; count: number }[];
}

export interface PatientStatsResponse {
  success: true;
  data: PatientStats;
}

export interface PatientUhidOption {
  id: string;
  label: string;
  code: string;
  phone: string;
  gender: Patient['gender'];
  dateOfBirth: string;
  address: string | null;
  state: string | null;
  district: string | null;
  pinCode: string | null;
  bloodGroup: Patient['bloodGroup'];
  uhid?: string | null;
  matchType?: 'patient' | 'bill';
  billNumber?: string;
  billModule?: string;
  recordId?: string;
}

export interface PatientUhidLookupResponse {
  success: true;
  data: PatientUhidOption[];
}

export interface PatientReference {
  id: string;
  name: string;
  code?: string;
}

export interface PatientSnapshot {
  uhid?: string | null;
  name: string;
  phone?: string | null;
  gender?: string;
  ageYears?: number;
  ageMonths?: number;
  ageDays?: number;
  address?: string | null;
  state?: string | null;
  district?: string | null;
  pinCode?: string | null;
  guardianName?: string | null;
  relation?: string | null;
  alternativeNumber?: string | null;
  aadhaar?: string | null;
}

export interface PatientCharge {
  _id?: string;
  chargeName: string;
  rate: number;
  quantity: number;
  discountPercent?: number;
  discountAmount?: number;
  amount: number;
  testStatus?: string;
  barcode?: string | null;
  sampleCollectedAt?: string | null;
  labReceivedAt?: string | null;
  reportGeneratedAt?: string | null;
}

export interface PatientPayment {
  mode: string;
  bankName?: string | null;
  amount: number;
}

export interface PatientBilling {
  billNumber: string;
  total: number;
  discountType?: string;
  totalDiscount: number;
  grandTotal: number;
  totalPayment: number;
  dueAmount: number;
  creditAmount?: number;
  billStatus: string;
  approved?: boolean;
}

export interface PatientHistoryRecord {
  id: string;
  createdAt?: string;
  updatedAt?: string;
  patientSnapshot?: PatientSnapshot;
  status?: string;
  type?: string;
  visitType?: string;
  appointmentDate?: string;
  bookingTime?: string;
  registrationDate?: string;
  billDate?: string;
  admissionDate?: string;
  scheduledDate?: string;
  issueDate?: string;
  admittedAt?: string;
  releasedAt?: string | null;
  enquiryNumber?: number;
  opdNumber?: number;
  ticketNumber?: number;
  emgNumber?: number;
  investigationNumber?: number;
  ipdNumber?: number;
  dialysisNumber?: number;
  otNumber?: number;
  issueNumber?: string;
  department?: PatientReference | null;
  doctor?: PatientReference | null;
  attendingDoctor?: PatientReference | null;
  referringDoctor?: PatientReference | null;
  underDoctor?: PatientReference | null;
  otherUnderDoctor?: PatientReference | null;
  surgeon?: PatientReference | null;
  anesthetist?: PatientReference | null;
  procedureType?: PatientReference | null;
  otRoom?: PatientReference | null;
  dialysisMachine?: PatientReference | null;
  bedCategory?: PatientReference | null;
  bed?: PatientReference | null;
  slot?: PatientReference | null;
  bookingBy?: PatientReference | null;
  generatedBy?: PatientReference | null;
  issuedBy?: PatientReference | null;
  referredBy?: PatientReference | null;
  provider?: PatientReference | null;
  marketBy?: PatientReference | null;
  donation?: PatientReference | null;
  charges?: PatientCharge[];
  payments?: PatientPayment[];
  billing?: PatientBilling;
  admissionType?: string;
  packageType?: string;
  insuranceType?: string;
  insuranceNumber?: string | null;
  symptoms?: string | null;
  diagnosisAtAdmission?: string | null;
  diagnosis?: string | null;
  notes?: string | null;
  alcoholTobaccoHistory?: string | null;
  pastMedicalSurgicalHistory?: string | null;
  familyHistory?: string | null;
  responsiblePerson?: { name?: string | null; relation?: string | null; phone?: string | null; age?: number | null; address?: string | null };
  startTime?: string;
  endTime?: string;
  patientName?: string;
  patientNumber?: string;
  section?: string;
  bagBarcode?: string;
  donorGroup?: string;
  recipientGroup?: string;
  crossmatchResult?: string;
  note?: string | null;
  source?: string;
  sourceRecordId?: string | null;
  usageStatus?: string;
  active?: boolean;
}

export interface PatientProfileBill {
  id: string;
  source: string;
  recordId: string;
  referenceNumber: string;
  billNumber: string;
  date: string;
  doctor: PatientReference | null;
  charges: PatientCharge[];
  payments: PatientPayment[];
  billing: PatientBilling;
  href: string;
}

export interface PatientProfileTest {
  id: string;
  billId: string;
  investigationNumber: string;
  billNumber: string;
  name: string;
  status: string;
  barcode: string | null;
  orderedAt: string;
  sampleCollectedAt: string | null;
  labReceivedAt: string | null;
  reportGeneratedAt: string | null;
  date: string;
  amount: number;
  href: string;
}

export interface PatientProfilePrescription {
  id: string;
  source: 'OPD' | 'IPD / Daycare' | 'EMG';
  recordId: string;
  recordNumber: string;
  prescriptionDate: string;
  diagnoses: string[];
  medicines: Array<{ medicine?: string | null; composition?: string | null; dose?: string | null; when?: string | null; frequency?: string | null; duration?: string | null; notes?: string | null }>;
  complaints: Array<{ complaint?: string | null; frequency?: string | null; severity?: string | null; duration?: string | null }>;
  testsNeeded: string | null;
  advice: string | null;
  createdBy: PatientReference | null;
  href: string;
  editHref: string;
}

export interface PatientTimelineItem {
  id: string;
  type: string;
  title: string;
  subtitle: string | null;
  date: string;
  status: string | null;
  href: string | null;
}

export interface PatientProfileSummary {
  appointments: number;
  opdVisits: number;
  emergencyVisits: number;
  tests: number;
  admissions: number;
  dialysis: number;
  otCases: number;
  bloodIssues: number;
  bills: number;
  billed: number;
  paid: number;
  due: number;
  lastActivityAt: string | null;
}

export interface PatientProfile {
  patient: Patient;
  summary: PatientProfileSummary;
  timeline: PatientTimelineItem[];
  appointments: PatientHistoryRecord[];
  bills: PatientProfileBill[];
  tests: PatientProfileTest[];
  opdRegistrations: PatientHistoryRecord[];
  emgRegistrations: PatientHistoryRecord[];
  investigationBills: PatientHistoryRecord[];
  ipdDaycareAdmissions: PatientHistoryRecord[];
  dialysisAdmissions: PatientHistoryRecord[];
  otCases: PatientHistoryRecord[];
  bloodIssues: PatientHistoryRecord[];
  bedOccupancies: PatientHistoryRecord[];
  prescriptions: PatientProfilePrescription[];
}

export interface PatientProfileResponse {
  success: true;
  data: PatientProfile;
}

export interface PatientCurrentStatus {
  ipd: { ipdNumber: string; admissionDate: string; admissionType: string | null; bedName: string | null } | null;
  dialysis: { dialysisNumber: string; admissionDate: string; startTime: string | null; endTime: string | null; bedName: string | null; wardName: string | null } | null;
  emg: { emgNumber: string; registrationDate: string } | null;
  ot: { otNumber: string; scheduledDate: string; roomName: string | null } | null;
}
